<!--
 * @author yuping.pang
 * @date 2023-10-07 14:18
 * @description  发货工作台-导入发货单结果+进度展示
-->
<template>
  <a-modal
    :visible="visible"
    :title="title"
    :maskClosable="false"
    :width="1200"
    :dialogStyle="{top:'10px'}"
    @cancel="onCancel"
  >
    <a-row key="step" type="flex" justify="center" class="invoice-import-step">
      <a-col span="12">
        <a-steps :current="stepVal">
          <a-step title="导入"></a-step>
          <a-step title="预览"></a-step>
          <a-step title="完成"></a-step>
        </a-steps>
      </a-col>
    </a-row>
    <a-row key="stepItem1" type="flex" justify="center" align="middle" class="upload-layout" v-if="stepVal === 0">
      <a-col>
        <div class="upload-main">
          <a-upload-dragger
            :file-list="fileList"
            :before-upload="beforeUpload"
            :remove="handleRemoveFile"
            accept=".xlsx,.xls"
            @reject="errorFileTypeHandler"
          >
            <p class="ant-upload-drag-icon">
              <a-icon type="inbox" />
            </p>
            <p class="ant-upload-text">
              点击或将文件拖拽到这里上传
            </p>
            <p class="ant-upload-hint">
              支持扩展名：.xlsx .xls
            </p>
          </a-upload-dragger>
          <p v-if="downloadTemplate" class="download-template"><a @click="onDownloadTemplate"><a-icon type="file-text" />模板下载</a></p>
        </div>
      </a-col>
    </a-row>
    <div key="stepItem2" v-if="stepVal === 1">
      <a-tabs type="card">
        <a-tab-pane key="2" tab="正常数据">
          <slot name="successTable" :dataList="successData" />
        </a-tab-pane>
        <a-tab-pane key="1" tab="异常数据">
          <slot name="errorTable" :dataList="errorData" />
        </a-tab-pane>
      </a-tabs>
    </div>
    <div key="stepItem3" class="result-step" v-if="stepVal === 2">
      <div v-if="(commitErrorData.length + errorData.length) > 0" key="errorResult">
        <a-result title="" status="warning">
          <p slot="subTitle">成功导入 <span class="success-font">{{ this.successCount }}</span> 行数据</p>
          <p slot="subTitle">
            导入失败 <span class="global-red-font">{{ errorData.length + commitErrorData.length }}</span> 行
          </p>
          <template #extra>
            <a-button @click="downloadErrorData('commitErrorData')" type="primary">下载错误数据</a-button>
            <a-button @click="closeModal">关闭</a-button>
          </template>
        </a-result>
      </div>
      <!--该功能暂未需要-未完成-->
      <div v-else-if="commitWarnData.length > 0" key="warnResult">
        <a-result
          status="warning"
          title="提示"
        >
          <p slot="subTitle">数据异常</p>
          <div slot="extra">
            <a-space>
              <a-button @click="downloadErrorData('commitWarnData', true)" type="primary">下载错误数据</a-button>
              <a-button @click="continueImport" :loading="confirmLoading">继续导入</a-button>
              <a-button @click="closeModal">关闭</a-button>
            </a-space>
          </div>
        </a-result>
      </div>
      <div v-else key="successResult">
        <a-result
          status="success"
          title="操作成功"
          :sub-title="`已成功导入 ${successCount} 行`"
        >
          <template #extra>
            <a-button key="continue" type="primary" @click="back2FirstStep">继续导入</a-button>
            <a-button key="back" @click="closeModal">关闭</a-button>
          </template>
        </a-result>
      </div>
    </div>
    <template #footer>
      <div class="import-invoice-footer">
        <a-space>
          <a-button v-if="stepVal === 0" key="cancel" @click="closeModal" :disabled="confirmLoading">取消</a-button>
          <a-button v-if="stepVal === 1" key="back2FirstStep" @click="back2FirstStep" :disabled="confirmLoading">上一步</a-button>
          <a-button
            v-if="stepVal === 0"
            :loading="confirmLoading"
            :disabled="fileList.length === 0"
            type="primary"
            key="next"
            @click="nextStepHandler"
          >下一步</a-button>
          <a-button v-if="stepVal === 1" :loading="confirmLoading" type="primary" key="submit" @click="submitHandler">开始导入</a-button>
        </a-space>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { IMPORT_STATE_MAP } from '../enum';
import { json2xlsx } from "@/utils/xlsxUtil";

export default {
  name: 'ImportResult',
  props: {
    downloadTemplate: { // 下载模板
      type: Function,
      default: null
    },
    importFileReadFunc: { // 文件解析数据接口函数
      type: Function,
      default: null
    },
    importApiFunc: { // 表格数据导入函数
      type: Function,
      default: null
    },
    resolveFileReadDataFunc: { // 处理解析的文件数据
      type: Function,
      default: null
    },
    errorExcelCol: {
      type: Array,
      default: () => {
        return []
      }
    },
    apiGetErrorFile: { // 导出错误文件的接口
      type: Function,
      default: null
    }
  },
  components: {},
  data () {
    return {
      visible: false,
      title: '导入收货计划',
      confirmLoading: false,
      stepVal: 0, // 步骤索引
      fileList: [],
      tableProps: {},
      successData: [],
      errorData: [],
      successCount: 0, // 导入成功数量
      commitErrorData: [], // 导入异常数量
      commitWarnData: [], // 导入警告数据，用于下载错误数据
      commitNormalData: [] // 导入出现警告数据，但拆解出正常的那部分数据
    }
  },
  methods: {
    // 打开窗口
    show () {
      this.visible = true;
    },
    onCancel () {
      // 完成步骤刷新列表
      if (this.stepVal === 2) {
        this.$emit('successCall');
      }
      this.visible = false;
    },
    // 下载模板
    onDownloadTemplate () {
      this.downloadTemplate();
    },
    // 拦截自动上传，且限制文件数为1
    beforeUpload (file) {
      this.fileList = [file];
      return false;
    },
    // 删除文件
    handleRemoveFile (file) {
      const index = this.fileList.indexOf(file);
      const newFileList = this.fileList.slice();
      newFileList.splice(index, 1);
      this.fileList = newFileList;
    },
    // 关闭并重置导入窗口
    closeModal () {
      if (this.stepVal === 2) {
        // 完成步骤触发成功回调
        this.$emit('successCall');
      }
      this.back2FirstStep();
      this.visible = false;
    },
    // 返回第一步
    back2FirstStep () {
      this.stepVal = 0;
      this.successData = [];
      this.errorData = [];
      this.commitErrorData = [];
      this.commitWarnData = [];
      this.commitNormalData = [];
      this.fileList = [];
      this.successCount = 0; // 导入成功数量
    },
    // 下一步
    nextStepHandler () {
      this.confirmLoading = true;
      const formData = new FormData();
      formData.append('file', this.fileList[0]);
      this.importFileReadFunc(formData).then(res => {
        let resData = res;
        if (this.resolveFileReadDataFunc) {
          resData = this.resolveFileReadDataFunc(res);
        }
        if (resData?.code === 200) {
          this.stepVal = 1;
          const { successDataList, errorDataList } = resData.data || {};
          // 添加状态信息
          this.successData = (successDataList || []).map(item => {
            item.importState = IMPORT_STATE_MAP.PENDING;
            return item;
          })
          this.errorData = errorDataList || []
        }
      })
      .finally(() => {
        this.confirmLoading = false;
      })
    },
    // 提交
    submitHandler () {
      const list = [...this.successData];
      this.confirmLoading = true;
      if (list.length > 0) {
        // 记录导入失败的行，如果有则发送请求导入失败的
        this.importBybatches(list);
      } else {
        this.confirmLoading = false;
        this.stepVal = 2;
      }
    },
    // 分批导入
    importBybatches (list) {
      if (list?.length > 0) {
        const promiseList = [];
        list.splice(0, 3).map(item => {
          item.importState = IMPORT_STATE_MAP.LOADING;
          const sendP = this.importApiFunc(item);
          promiseList.push(sendP);
          sendP.then(res => {
            const { code } = res;
            if (code === 200) {
              // this.commitWarnData = warnData;
              // this.commitErrorData = errorData;
              // this.commitNormalData = normalData;
              // this.successCount = successCount;
              item.importState = IMPORT_STATE_MAP.FINISHED;
              this.successCount += 1;
            } else {
              throw res;
            }
          }).catch((err) => {
            const msg = err?.data?.msg || err?.data?.message || err?.data?.error || '导入失败';
            this.commitErrorData.push({ ...item, errorInfo: msg });
            item.importState = IMPORT_STATE_MAP.FAIL;
          })
        })
        Promise.allSettled(promiseList).then(() => {
          this.importBybatches(list);
        })
        .finally(() => {
          if (list.length === 0) {
            this.confirmLoading = false;
            this.stepVal = 2;
          }
        })
      }
    },
    // 拖拽错误类型进行警告
    errorFileTypeHandler () {
      this.$message.warning('文件类型错误，仅接受.xls、.xlsx扩展名');
    },
    // 下载错误文件
    downloadErrorData (filed) {
      if (this.apiGetErrorFile) {
        this.apiGetErrorFile([...this[filed], ...this.errorData]);
      } else {
        json2xlsx(
          [...this[filed], ...this.errorData],
          [
            {
              dataIndex: 'errorInfo',
              title: "",
              styleCB: () => ({
                font: { color: { rgb: "FF0000" } }
              })
            },
            ...this.errorExcelCol
          ],
          '错误数据.xlsx'
        );
      }
    },
    // 数据有误，但继续导入
    continueImport () {
      // this.confirmLoading = true;
      // apiImportWarnSubmit({
      //   ...this.$route.params,
      //   warnData: this.commitWarnData.concat(this.commitNormalData)
      // }).then(res => {
      //   const {state, items} = res.data;
      //   if (state === 100) {
      //     this.successCount = items;
      //     this.commitWarnData = [];
      //     this.commitErrorData = [];
      //     this.commitNormalData = [];
      //   }
      // }).finally(() => {
      //   this.confirmLoading = false;
      // })
    }
  }
}
</script>

<style lang="less" scoped>
.invoice-import-step{
  margin-top: 30px;
}
.result-step {
  height: calc(100vh - 210px);
}
.upload-layout {
  height: calc(100vh - 250px);
  .upload-main {
    width: 400px;
    padding-top: 10px;
    padding-bottom: 120px;
    .download-template {
      margin-top: 10px;
    }
  }
}
.import-invoice-footer {
  text-align: right;
}
/deep/.small-margin-top {
  margin-top: 10px;
}
.invoice-import-select {
  /deep/ .ant-select-selection{
    min-height: 32px;
    height: auto;
    .ant-select-selection-selected-value {
      float: none;
    }
  }
}
.success-font {
  color: #007EF9;
}
</style>
